import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import SignatureCanvas from 'react-signature-canvas';
import { Bold, Italic, RotateCcw, RotateCw, Upload, Wand2, Keyboard, PenTool, Eraser, Palette, Loader2, Sparkles, X, Undo2, Redo, Copy } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import GrammarErrors from './GrammarErrors';
import GrammarTooltip from './GrammarTooltip';
import type { UISuggestion } from '../../types/grammar';

const TAMIL_EDITOR_STORAGE_KEY = 'tamil_editor_content';

const PEN_COLORS = [
  { name: 'Black', hex: '#1e293b' },
  { name: 'Indigo', hex: '#4f46e5' },
  { name: 'Rose', hex: '#e11d48' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Amber', hex: '#f59e0b' },
];

export type TamilEditorProps = {
  content: string;
  suggestions: UISuggestion[];
  focusedId: string | null;
  isLoading?: boolean;
  showCorrectedPanel: boolean;
  onContentChange: (nextContent: string) => void;
  onCheckGrammar: (text: string) => Promise<void> | void;
  onUploadFile: (file: File) => Promise<void> | void;
  renderEmbed?: (args: { toolbarLeft: React.ReactNode; editorContent: React.ReactNode }) => React.ReactNode;
};

const applyCorrections = (originalText: string, suggestions: UISuggestion[]): string => {
  if (!originalText.trim() || suggestions.length === 0) return originalText;

  let corrected = originalText;
  const ranged = suggestions
    .filter(item => typeof item.start === 'number' && typeof item.end === 'number' && item.end > item.start)
    .sort((a, b) => (b.start ?? 0) - (a.start ?? 0));

  if (ranged.length > 0) {
    ranged.forEach((item) => {
      const start = item.start as number;
      const end = item.end as number;
      corrected = corrected.slice(0, start) + item.suggestion + corrected.slice(end);
    });
    return corrected;
  }

  const fallback = [...suggestions];
  fallback.forEach((item) => {
    if (!item.original) return;
    corrected = corrected.replace(item.original, item.suggestion);
  });
  return corrected;
};

export const TamilEditor: React.FC<TamilEditorProps> = ({
  content,
  suggestions,
  focusedId,
  isLoading = false,
  showCorrectedPanel, // Note: We now handle the split view internally!
  onContentChange,
  onCheckGrammar,
  onUploadFile,
  renderEmbed,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 🌟 WHITE iPAD MODAL STATES 🌟
  const canvasRef = useRef<SignatureCanvas>(null);
  const [isDrawModalOpen, setIsDrawModalOpen] = useState(false);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const [penColor, setPenColor] = useState(PEN_COLORS[1].hex);
  const [canvasHistory, setCanvasHistory] = useState<unknown[]>([]);
  const [canvasRedoHistory, setCanvasRedoHistory] = useState<unknown[]>([]);
  const [isUploadingCanvas, setIsUploadingCanvas] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, GrammarErrors],
    content,
    editorProps: {
      attributes: {
        // Removed min-height here so the flexbox layout controls the height dynamically
        class: 'prose prose-indigo dark:prose-invert max-w-none focus:outline-none w-full text-app-text dark:text-slate-100 p-4 h-full',
      },
    },
    onUpdate: ({ editor: tiptapEditor }) => {
      const text = tiptapEditor.getText();
      localStorage.setItem(TAMIL_EDITOR_STORAGE_KEY, text);
      onContentChange(text);
    },
  });

  // Tooltip state for grammar marks
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;

    const onMouseOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('.grammar-error-line') as HTMLElement | null;
      if (!target) return;
      const text = target.dataset.tooltip ?? '';
      if (!text) return;
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      const rect = target.getBoundingClientRect();
      setTooltipContent(text);
      setTooltipRect(rect);
      setTooltipVisible(true);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!tooltipVisible) return;
      const target = (e.target as HTMLElement).closest('.grammar-error-line') as HTMLElement | null;
      if (!target) {
        // hide after a short delay
        if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = window.setTimeout(() => {
          setTooltipVisible(false);
          hoverTimeoutRef.current = null;
        }, 80);
        return;
      }
      const rect = target.getBoundingClientRect();
      setTooltipRect(rect);
    };

    const onMouseOut = (e: MouseEvent) => {
      const related = (e as MouseEvent & { relatedTarget?: Node }).relatedTarget as HTMLElement | null;
      // if moving to another mark, don't hide
      if (related && related.closest && (related as HTMLElement).closest('.grammar-error-line')) return;
      if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = window.setTimeout(() => {
        setTooltipVisible(false);
        hoverTimeoutRef.current = null;
      }, 80);
    };

    dom.addEventListener('mouseover', onMouseOver);
    dom.addEventListener('mousemove', onMouseMove);
    dom.addEventListener('mouseout', onMouseOut);

    const onWindowResize = () => {
      if (!tooltipVisible) return;
      if (tooltipRect) setTooltipRect(tooltipRect);
    };
    window.addEventListener('scroll', onWindowResize, true);
    window.addEventListener('resize', onWindowResize);

    return () => {
      dom.removeEventListener('mouseover', onMouseOver);
      dom.removeEventListener('mousemove', onMouseMove);
      dom.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onWindowResize, true);
      window.removeEventListener('resize', onWindowResize);
    };
  }, [editor, tooltipVisible, tooltipRect]);

  useEffect(() => {
    if (!editor) return;
    if (editor.getText() !== content) {
      editor.commands.setContent(content || '', { emitUpdate: false });
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    // @ts-ignore
    editor.commands.setSuggestions?.(suggestions);
  }, [editor, suggestions]);

  useEffect(() => {
    if (!editor) return;
    // @ts-ignore
    editor.commands.setFocusedId?.(focusedId);
  }, [editor, focusedId]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!editor) return;
      editor.commands.setContent(detail || '', { emitUpdate: false });
    };
    window.addEventListener('tamil-ocr-text', handler as EventListener);
    return () => window.removeEventListener('tamil-ocr-text', handler as EventListener);
  }, [editor]);

  // Fix canvas rendering perfectly when modal opens
  useEffect(() => {
    if (isDrawModalOpen) {
      setTimeout(() => {
        canvasRef.current?.clear();
        setIsCanvasEmpty(true);
        setCanvasHistory([]);
        setCanvasRedoHistory([]);
      }, 50);
    }
  }, [isDrawModalOpen]);

  const correctedText = useMemo(() => applyCorrections(content, suggestions), [content, suggestions]);

  const handleCheckGrammar = async () => {
    if (!editor) return;
    await onCheckGrammar(editor.getText());
  };

  const handleUploadClick = () => { fileInputRef.current?.click(); };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onUploadFile(file);
    event.target.value = '';
  };

  const handleCopyCorrected = () => {
    const textToCopy = correctedText || content;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    }
  };

  // 🌟 CANVAS DRAWING LOGIC 🌟
  const handleCanvasEnd = () => {
    if (canvasRef.current) {
      const data = canvasRef.current.toData();
      setCanvasHistory(data);
      setCanvasRedoHistory([]);
      setIsCanvasEmpty(data.length === 0);
    }
  };

  const handleCanvasUndo = () => {
    if (canvasHistory.length > 0) {
      const newHistory = canvasHistory.slice(0, -1);
      const popped = canvasHistory[canvasHistory.length - 1];
      setCanvasRedoHistory((r) => [...r, popped]);
      setCanvasHistory(newHistory);
      canvasRef.current?.fromData(newHistory as never);
      setIsCanvasEmpty(newHistory.length === 0);
    }
  };

  const handleCanvasRedo = () => {
    if (canvasRedoHistory.length > 0) {
      const toRestore = canvasRedoHistory[canvasRedoHistory.length - 1];
      const newRedo = canvasRedoHistory.slice(0, -1);
      const newHistory = [...canvasHistory, toRestore];
      setCanvasHistory(newHistory);
      setCanvasRedoHistory(newRedo);
      canvasRef.current?.fromData(newHistory as never);
      setIsCanvasEmpty(false);
    }
  };

  const handleCanvasClear = () => {
    canvasRef.current?.clear();
    setIsCanvasEmpty(true);
    setCanvasHistory([]);
    setCanvasRedoHistory([]);
  };

  const handleCanvasSubmit = async () => {
    if (!canvasRef.current || canvasRef.current.isEmpty()) return;
    const dataURL = canvasRef.current.getCanvas().toDataURL('image/png');
    const blob = await (await fetch(dataURL)).blob();
    const file = new File([blob], 'handwriting.png', { type: 'image/png' });
    
    setIsUploadingCanvas(true);
    try {
      await onUploadFile(file);
      setIsDrawModalOpen(false); 
    } finally {
      setIsUploadingCanvas(false);
    }
  };

  // 🌟 TOOLBAR RENDER 🌟
  const toolbarLeft = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-700/50 p-1 bg-slate-50 dark:bg-slate-800/50">
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
          aria-label="Type text"
        >
          <Keyboard className="w-4 h-4" /> Type
        </button>
        <button
          type="button"
          onClick={() => setIsDrawModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all text-slate-500 dark:text-slate-400 hover:text-fuchsia-600 dark:hover:text-fuchsia-400"
          aria-label="Draw Tamil"
        >
          <PenTool className="w-4 h-4" /> Draw
        </button>
      </div>

      <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />

      <button type="button" className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50" onClick={() => editor?.chain().focus().toggleBold().run()} disabled={!editor}><Bold className="w-4 h-4" /></button>
      <button type="button" className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50" onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={!editor}><Italic className="w-4 h-4" /></button>
      <button type="button" className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50" onClick={() => editor?.chain().focus().undo().run()} disabled={!editor}><RotateCcw className="w-4 h-4" /></button>
      <button type="button" className="h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-50" onClick={() => editor?.chain().focus().redo().run()} disabled={!editor}><RotateCw className="w-4 h-4" /></button>
    </div>
  );

  // 🌟 THE WHITE iPAD POPUP 🌟
  const DrawModal = isDrawModalOpen && (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-5xl h-[85vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200 relative">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center z-10">
          <div className="flex items-center gap-6">
            <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
              <PenTool className="w-6 h-6 text-indigo-600" /> Magic Whiteboard
            </h3>
            <div className="flex items-center gap-2 bg-white p-2 rounded-full border border-slate-200 shadow-sm">
              <Palette className="w-4 h-4 text-slate-400 ml-2" />
              {PEN_COLORS.map(c => (
                <button 
                  key={c.name} onClick={() => setPenColor(c.hex)} 
                  className={`w-6 h-6 rounded-full transition-transform border border-slate-200 ${penColor === c.hex ? 'scale-125 ring-2 ring-indigo-500 shadow-md' : 'hover:scale-110'}`} 
                  style={{ backgroundColor: c.hex }} title={c.name}
                />
              ))}
            </div>
          </div>
          <button onClick={() => setIsDrawModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 bg-white relative cursor-crosshair">
          <SignatureCanvas 
            ref={canvasRef} penColor={penColor} minWidth={2} maxWidth={5} onEnd={handleCanvasEnd}
            canvasProps={{ className: 'w-full h-full absolute inset-0' }} 
          />
          {isCanvasEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-40">
              <div className="text-center text-slate-400">
                <PenTool className="w-12 h-12 mx-auto mb-2 text-indigo-300" />
                <p className="text-sm font-bold uppercase tracking-widest">Draw Tamil Here</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={handleCanvasUndo} disabled={canvasHistory.length === 0} className="border-slate-300 text-slate-700 hover:bg-slate-100 font-bold px-6 bg-white shadow-sm disabled:opacity-50"><Undo2 className="w-5 h-5 mr-2" /> Undo</Button>
            <Button variant="outline" size="lg" onClick={handleCanvasRedo} disabled={canvasRedoHistory.length === 0} className="border-slate-300 text-slate-700 hover:bg-slate-100 font-bold px-6 bg-white shadow-sm disabled:opacity-50 hidden sm:flex"><Redo className="w-5 h-5 mr-2" /> Redo</Button>
            <Button variant="outline" size="lg" onClick={handleCanvasClear} className="border-rose-200 text-rose-600 hover:bg-rose-50 font-bold px-6 bg-white shadow-sm"><Eraser className="w-5 h-5 mr-2" /> Clear</Button>
          </div>
          <Button size="lg" onClick={handleCanvasSubmit} disabled={isCanvasEmpty || isUploadingCanvas || isLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-10 py-6 text-lg shadow-lg shadow-indigo-600/30 disabled:opacity-50">
            {isUploadingCanvas || isLoading ? <Loader2 className="w-6 h-6 animate-spin mr-2" /> : <Sparkles className="w-6 h-6 mr-2" />}
            {isUploadingCanvas || isLoading ? "Evaluating..." : "Evaluate Grammar ✨"}
          </Button>
        </div>
      </div>
    </div>
  );

  // 🌟 SPLIT VIEW: ORIGINAL DRAFT & CORRECTED GRAMMAR 🌟
  const editorContentComponent = (
    <div className="flex flex-col h-full w-full">
      
      {/* 1. Top Section: Original Draft */}
      <div className="flex-1 flex flex-col min-h-[220px]">
        <div className="px-4 pt-4 flex justify-between items-center">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Original Draft
          </span>
          {suggestions.length > 0 && (
            <span className="bg-rose-100 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
              {suggestions.length} Errors
            </span>
          )}
        </div>
        <EditorContent editor={editor} className="flex-1 w-full overflow-y-auto custom-scrollbar" />
      </div>

      {/* Split Divider */}
      {/* <div className="h-px w-full bg-slate-200 dark:bg-slate-700/60" /> */}

      {/* 2. Bottom Section: Corrected Grammar */}
      {/* <div className="flex-1 flex flex-col min-h-[220px] bg-emerald-50/30 dark:bg-emerald-950/20">
        <div className="px-4 pt-4 pb-2 flex justify-between items-center">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Corrected Grammar
          </span>
          <button 
            onClick={handleCopyCorrected}
            className="flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors bg-white dark:bg-emerald-900/30 px-2.5 py-1 rounded-md border border-emerald-200 dark:border-emerald-800"
            title="Copy Corrected Text"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
        <div className="flex-1 p-4 pt-0 overflow-y-auto custom-scrollbar">
          <p className="whitespace-pre-wrap break-words tamil-text text-slate-800 dark:text-slate-200 text-lg leading-relaxed">
            {correctedText || content || (
              <span className="text-slate-400 dark:text-slate-500 italic text-base">
                Corrected text will appear here after grammar check...
              </span>
            )}
          </p>
        </div>
      </div> */}

      {DrawModal}
    </div>
  );

  if (renderEmbed) {
    return (
      <>
        {renderEmbed({ toolbarLeft, editorContent: editorContentComponent })}
        <GrammarTooltip content={tooltipContent} rect={tooltipRect} visible={tooltipVisible} />
      </>
    );
  }

  // Fallback standalone render
  return (
    <>
      <Card className="shadow-lg border border-app-border dark:border-slate-700 flex flex-col h-full">
      <CardHeader className="mb-0 pb-0 shrink-0">
        <CardTitle className="text-xl">Tamil Grammar Assistant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 flex flex-col min-h-0">
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {toolbarLeft}
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" onClick={handleCheckGrammar} disabled={isLoading}>
              <Wand2 className="w-4 h-4 mr-2" /> Check Grammar
            </Button>
            <Button type="button" variant="secondary" onClick={handleUploadClick} disabled={isLoading}>
              <Upload className="w-4 h-4 mr-2" /> Upload
            </Button>
            <input ref={fileInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleFileSelected} />
          </div>
        </div>

        <div className="flex-1 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden min-h-0 flex flex-col">
          {editorContentComponent}
        </div>
      </CardContent>
      </Card>
      <GrammarTooltip content={tooltipContent} rect={tooltipRect} visible={tooltipVisible} />
    </>
  );
};

export default TamilEditor;